import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, effect, inject } from '@angular/core';

import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { tap } from 'rxjs';

export interface Config {
  apiUrl: string;
  licoHosts: string[];
}

export interface ConfigState {
  loaded: boolean;
  config: Config;
}

const state: ConfigState = {
  config: {
    apiUrl: '',
    licoHosts: []
  },
  loaded: false
};

export const ClientConfigStore = signalStore(
  { providedIn: 'root' },
  withState<ConfigState>(state),
  withProps(() => ({
    http: inject(HttpClient),
    document: inject(DOCUMENT)
  })),
  withComputed((state) => ({
    isLicoOrigin: computed(() => state.config.licoHosts().includes(state.document.location.host)),
    clientId: computed(() => (state.config.licoHosts().includes(state.document.location.host) ? 'lico' : 'lion'))
  })),
  withMethods((state) => {
    return {
      loadConfig(): void {
        if (state.loaded()) {
          return;
        }

        state.http
          .get<{ licoHosts: string[] }>('/config/lico-config.json')
          .pipe(
            tap((licoConfig) => {
              const hostname = state.document.location.hostname;
              const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
              const effectiveApiUrl = isLocalhost ? 'http://localhost:8080' : state.document.location.origin;

              const mergedConfig: Config = {
                apiUrl: effectiveApiUrl,
                licoHosts: licoConfig.licoHosts || []
              };
              patchState(state, { config: mergedConfig, loaded: true });
            })
          )
          .subscribe({
            error: (err) => {
              console.error(`Error while loading config: ${err.message}`);
            }
          });
      },

      // Sonderfall für einen Store, damit der Store in der Initialisierung der Anwendung funktioniert.
      waitForConfigLoad(): Promise<void> {
        return new Promise<void>((resolve) => {
          if (state.loaded()) {
            resolve();
            return;
          }
          const disposer = effect(() => {
            if (state.loaded()) {
              resolve();
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              // effect abmelden
              disposer();
            }
          });
        });
      }
    };
  })
);
